import { AxiosResponse } from 'axios';

import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';

export interface SpecialtyParty {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  /** Site/location notes (e.g. MomentDash locationDetails.notes). */
  notes?: string;
}

export interface SpecialtyPiece {
  weight: number;
  weightUnit?: 'LB' | 'KG';
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: 'IN' | 'CM';
  /** Identical packages of this size/weight (Mercury/BioCair). */
  quantity?: number;
}

export interface SpecialtyProvider {
  provider: string;
  courierId: string;
  title: string;
  requiresPieces: boolean;
}

export interface SpecialtyOptionChoice {
  value: string | number;
  label: string;
}

/** Where a provider-specific field should render in the specialty form. */
export type SpecialtyFieldSection =
  'pickup' | 'delivery' | 'shipment' | 'packages' | 'provider';

export interface SpecialtyOptionField {
  key: string;
  label: string;
  type: 'select' | 'text' | string;
  required?: boolean;
  optionsKey?: string | null;
  options?: SpecialtyOptionChoice[];
  /** Prefill shown in the form (e.g. BioCair DG default). */
  default?: string | number | boolean;
  placeholder?: string;
  /** Form category; defaults to ``provider`` when omitted. */
  section?: SpecialtyFieldSection;
  /**
   * When set, choices come from the selected parent option row
   * (e.g. MomentDash serviceTypes nested under accounts).
   */
  optionsDependOn?: string;
  /** Top-level options response key for the parent list (e.g. ``accounts``). */
  optionsParentKey?: string;
  /** Property on the parent option row that holds child choices. */
  optionsFromKey?: string;
}

export interface SpecialtyOptionsResponse {
  provider: string;
  requiresPieces?: boolean;
  fields?: SpecialtyOptionField[];
  [key: string]: unknown;
}

export interface SpecialtyBookPayload {
  provider: string;
  shipper: SpecialtyParty;
  recipient: SpecialtyParty;
  readyAt?: string;
  /** Pickup window end (ISO). Used by Mercury; BioCair pickup end when set. */
  readyUntil?: string;
  /** Optional delivery/dropoff ready time (ISO). MomentDash dropoff readyTime. */
  deliveryReadyAt?: string;
  /** Optional delivery window end (ISO). BioCair deliveryWindowEnd. */
  deliveryUntil?: string;
  reference?: string;
  specialInstructions?: string;
  deliveryInstructions?: string;
  contents?: string;
  pieces?: SpecialtyPiece[];
  notifyEmails?: string[];
  providerFields?: Record<string, unknown>;
  confirmDuplicate?: boolean;
}

export interface SpecialtyBookResponse {
  trackingNumber?: string | null;
  masterTrackingNumber?: string | null;
  carrierBookingId?: string | null;
  linkCode?: string | null;
  raw?: unknown;
}

interface CustomResponseEnvelope<T> {
  data: T;
  is_success: boolean;
  message?: string;
  status?: number;
}

export function unwrapSpecialtyPayload<T extends object>(
  res: AxiosResponse<unknown>
): T | undefined {
  const body = res?.data as Record<string, unknown> | undefined;
  const envelope = body?.data as Record<string, unknown> | undefined;
  if (!envelope) return undefined;
  const nested = envelope.data;
  if (nested !== undefined && nested !== null && typeof nested === 'object') {
    return nested as T;
  }
  return envelope as T;
}

export const GetSpecialtyProviders = (
  token: string
): Promise<
  AxiosResponse<CustomResponseEnvelope<{ providers: SpecialtyProvider[] }>>
> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.specialtyProviders, token)
      .then((res) =>
        resolve(
          res as AxiosResponse<
            CustomResponseEnvelope<{ providers: SpecialtyProvider[] }>
          >
        )
      )
      .catch((error) => reject(error?.response ?? error));
  });
};

export const GetSpecialtyOptions = (
  token: string,
  provider: string
): Promise<AxiosResponse<CustomResponseEnvelope<SpecialtyOptionsResponse>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.specialtyOptions}?provider=${encodeURIComponent(provider)}`,
        token
      )
      .then((res) =>
        resolve(
          res as AxiosResponse<CustomResponseEnvelope<SpecialtyOptionsResponse>>
        )
      )
      .catch((error) => reject(error?.response ?? error));
  });
};

export const BookSpecialtyShipment = (
  token: string,
  payload: SpecialtyBookPayload,
  idempotencyKey?: string
): Promise<AxiosResponse<CustomResponseEnvelope<SpecialtyBookResponse>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.specialtyBook,
        payload,
        token,
        undefined,
        undefined,
        'keep-auth',
        idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
      )
      .then((res) =>
        resolve(
          res as AxiosResponse<CustomResponseEnvelope<SpecialtyBookResponse>>
        )
      )
      .catch((error) => reject(error?.response ?? error));
  });
};
